import { sectionGroup, type SectionKind } from './price-calc.js';

/**
 * Wzór F „Sygnał” (owner 2026-10-30): what the print page shows, from the server's print data of a version and what
 * the user may see of its event. Pure: every number is the quote's own (line amounts, VAT, gross from the server);
 * this only sums sections, splits them into the equipment and the services (Obsługa and Transport; owner 2026-10-31: the discount once under the equipment), and says it in Polish. What is
 * missing is left out — never a placeholder.
 *
 * Shared (2026-10-31): the web's „Podgląd wydruku” and the server's PDF (modules/quotes `pdf.ts`) build the same model,
 * so the two never say different things. Dates come as ISO strings (JSON) or `Date` (server).
 */

/** 123450 -> "1 234,50 zł" (no-break spaces, „−” below zero; as the web's `formatPln`). */
export function formatPln(grosze: number): string {
  if (grosze < 0) return `−${formatPln(-grosze)}`;
  const zl = String(Math.floor(grosze / 100)).replace(/\B(?=(\d{3})+$)/g, '\u00a0');
  return `${zl},${String(grosze % 100).padStart(2, '0')}\u00a0zł`;
}

export const ACCENT = '#FFC534';
const INK = '#0E0E0F';
/** The greys after the first (black) section; many sections go round them again. */
const GREYS = ['#6F6F6F', '#BDBDBD', '#9A9A9A', '#4A4A4A', '#D6D6D6'];

/**
 * `zero`: the quantity is 0 — set by the server before the fields the user may not see are removed, so „Ukryj zera”
 * works also when the quantity is hidden (review 2026-10-31).
 */
export type PrintLine = {
  type: string;
  zero?: boolean;
  name?: string;
  description?: string;
  quantity?: number;
  daysMilli?: number;
  unitPriceGrosze?: number;
  discountBp?: number;
  netGrosze?: number;
  /**
   * The line's amount without its discount (`lineFullGrosze`), set by the server only for a role that sees the price
   * and the discount (rule 7a). Without it the line prints its net amount and no discount is shown anywhere.
   */
  fullGrosze?: number;
  /**
   * A transport line (priced by distance, or the tolls) — set by the server (`printLineOf`), as the offer counts it
   * (`offerCategory`): it prints under USŁUGI also from an equipment section of a version before P8.
   */
  transportLine?: true;
};
export type PrintTotals = { netGrosze: number; vatGrosze: number; grossGrosze: number; discountsGrosze: number };
export type PrintCompany = { name: string; nip?: string; addressL1?: string; addressL2?: string; phone?: string; email?: string; www?: string };
export type VersionState = 'robocza' | 'wyslana' | 'zaakceptowana' | 'odrzucona';

/** `GET /api/quotes/versions/:id/print`. */
export type QuotePrint = {
  eventId: string;
  subtitle: string;
  version: { id: string; number: number; state: VersionState; vatBp: number; hideZeroLines: boolean; sentAt: string | Date | null };
  versions: Array<{ id: string; number: number; state: VersionState }>;
  printedAt: string | Date;
  validDays: number;
  terms: string;
  sections: Array<{ name: string; kind: SectionKind | string; lines: PrintLine[] }>;
  totals: PrintTotals | null;
  company: PrintCompany;
};

/** The event as the user may see it (each part absent when hidden or not there). */
export type PrintEvent = {
  name?: string;
  client?: string | null;
  startsAt?: string | Date;
  endsAt?: string | Date;
  location?: string;
  locationAddress?: string;
  /** Names of the scopes of work. */
  scopes?: string[];
};

export type PrintRow = { name: string; desc: string; qty: string; price: string; value: string };
export type PrintSection = { title: string; short: string; fill: string; sum: string; sumGrosze: number; people: boolean; rows: PrintRow[] };
export type InfoCell = { label: string; lines: string[] };
/** A row of the small block under a group („Razem sprzęt”, „Rabat 10%”, „Sprzęt po rabacie”); `strong`: bold. */
export type PrintSummaryRow = { label: string; amount: string; strong?: true };
/**
 * Owner 2026-10-31 („rabat na końcu”): the quote in two groups. „SPRZĘT”: its sections, each with its head; „USŁUGI”
 * (`list`): Obsługa and Transport as one list — a single section whose head is the group's own. The lines at their
 * full amounts; `summary`: the sum, the discount and the sum after it under the group.
 */
export type PrintGroup = { key: 'equipment' | 'services'; title: string; list: boolean; sections: PrintSection[]; summary: PrintSummaryRow[] };

export type PrintModel = {
  number: string;
  draft: boolean;
  date: string;
  validUntil: string | null;
  eyebrow: string | null;
  title: string;
  client: string | null;
  bigDate: string | null;
  dateNote: string | null;
  info: InfoCell[];
  structure: { total: string; parts: Array<{ short: string; pct: string; share: number; fill: string }> } | null;
  groups: PrintGroup[];
  /** `big`: „Razem netto” a step larger than the other rows (owner 2026-10-31), still far below „DO ZAPŁATY”. */
  totals: { rows: Array<{ label: string; amount: string; big?: true }>; gross: string } | null;
  terms: string;
  company: string[];
};

const TZ = 'Europe/Warsaw';
const fmt = (o: Intl.DateTimeFormatOptions) => new Intl.DateTimeFormat('pl-PL', { timeZone: TZ, ...o });
const longDate = fmt({ day: 'numeric', month: 'long', year: 'numeric' });
const dayMonth = fmt({ day: '2-digit', month: '2-digit' });
const fullDay = fmt({ day: '2-digit', month: '2-digit', year: 'numeric' });
const weekday = fmt({ weekday: 'long' });
const year = fmt({ year: 'numeric' });
const time = fmt({ hour: '2-digit', minute: '2-digit', hour12: false });
const dayKey = fmt({ year: 'numeric', month: '2-digit', day: '2-digit' });

/** An amount without „zł” (the columns of the rows). */
const amount = (grosze: number) => formatPln(grosze).replace(/ zł$/, '');
/** 1500 → "1,5", 12 → "12". */
const num = (n: number) => String(Math.round(n * 1000) / 1000).replace('.', ',');
export const pctOf = (bp: number) => `${num(bp / 100)}%`;
/** „1 dzień”, „2 dni”, „1,5 dnia”. */
const daysWord = (days: number) => (days === 1 ? '1 dzień' : Number.isInteger(days) ? `${num(days)} dni` : `${num(days)} dnia`);

/** "1", "4 × 1,5 dnia", "2 dni" (one piece for more days), "0,5". */
function qtyText(l: PrintLine): string {
  if (l.quantity === undefined) return '';
  const days = l.daysMilli === undefined ? 1 : l.daysMilli / 1000;
  if (days === 1) return num(l.quantity);
  return l.quantity === 1 ? daysWord(days) : `${num(l.quantity)} × ${daysWord(days)}`;
}

/**
 * The amount a line prints: without its discount when the server gave it and the role sees the price and the
 * discount (rule 7a: never a discount the role may not see), otherwise its net amount.
 */
function shownFull(l: PrintLine): number | undefined {
  return l.fullGrosze !== undefined && l.discountBp !== undefined && l.unitPriceGrosze !== undefined ? l.fullGrosze : undefined;
}
/** What a line prints in grosze (its full amount when shown, else its net). */
export const lineAmount = (l: PrintLine): number => shownFull(l) ?? l.netGrosze ?? 0;
const fullOf = lineAmount;

function rowOf(l: PrintLine): PrintRow {
  const value = shownFull(l) ?? l.netGrosze;
  return { name: l.name ?? '', desc: l.description?.trim() ?? '', qty: qtyText(l), price: l.unitPriceGrosze === undefined ? '' : amount(l.unitPriceGrosze), value: value === undefined ? '' : amount(value) };
}

/** A group's money from its printed lines: full, net, what the lines take off and the one percentage they share (else null). */
export type GroupMoney = { fullGrosze: number; netGrosze: number; discountGrosze: number; commonBp: number | null };
export function groupMoney(lines: readonly PrintLine[]): GroupMoney {
  let full = 0;
  let net = 0;
  const bps = new Set<number>();
  for (const l of lines) {
    const f = fullOf(l);
    full += f;
    net += l.netGrosze ?? 0;
    // as `groupSummary`: lines worth nothing before the discount do not decide the percentage
    if (f > 0) bps.add(shownFull(l) === undefined ? 0 : (l.discountBp ?? 0));
  }
  const [only] = bps;
  return { fullGrosze: full, netGrosze: net, discountGrosze: full - net, commonBp: bps.size === 1 ? only! : null };
}

/** „Rabat 10%” when one percentage describes the discount, else „Rabat”. */
export const discountLabel = (bp: number | null): string => (bp ? `Rabat ${pctOf(bp)}` : 'Rabat');

/**
 * The block under a group: „Razem sprzęt” alone without a discount; else with the discount below zero and the sum
 * after it (bold). `discounts`: the rows taking off (label, grosze > 0), `after`: the sum after them.
 */
export function summaryRows(names: { total: string; after: string }, fullGrosze: number, discounts: ReadonlyArray<{ label: string; grosze: number }>, afterGrosze: number): PrintSummaryRow[] {
  const off = discounts.filter((d) => d.grosze !== 0);
  if (off.length === 0) return [{ label: names.total, amount: formatPln(fullGrosze), strong: true }];
  return [{ label: names.total, amount: formatPln(fullGrosze) }, ...off.map((d) => ({ label: d.label, amount: formatPln(-d.grosze) })), { label: names.after, amount: formatPln(afterGrosze), strong: true }];
}

export const SPRZET = { total: 'Razem sprzęt', after: 'Sprzęt po rabacie' };
export const USLUGI = { total: 'Razem usługi', after: 'Usługi po rabacie' };

/** The lines a section prints („Ukryj zera”: `zero`, else a quantity of 0). */
export const printedLines = (lines: readonly PrintLine[], hideZeroLines: boolean): PrintLine[] => lines.filter((l) => !(hideZeroLines && (l.zero ?? l.quantity === 0)));

/** The services as one list (owner 2026-10-31: „zostaw jak jest”): a section under the group's own head. */
export function serviceList(lines: readonly PrintLine[]): PrintSection {
  const sumGrosze = lines.reduce((a, l) => a + fullOf(l), 0);
  return { title: 'Usługi', short: 'Usługi', fill: ACCENT, sum: formatPln(sumGrosze), sumGrosze, people: true, rows: lines.map(rowOf) };
}

/** Whole percentages that always add up to 100 (largest remainder); `values` are positive. */
function percents(values: number[]): number[] {
  const total = values.reduce((a, b) => a + b, 0);
  const raw = values.map((v) => (v * 100) / total);
  const out = raw.map(Math.floor);
  let left = 100 - out.reduce((a, b) => a + b, 0);
  const order = raw.map((r, i) => [r - Math.floor(r), i] as const).sort((a, b) => b[0] - a[0] || a[1] - b[1]);
  for (const [, i] of order) {
    if (left <= 0) break;
    out[i]! += 1;
    left -= 1;
  }
  return out;
}

const firstWord = (name: string) => name.trim().split(/\s+/)[0] ?? '';

/**
 * The sections with their rows, colours and sums as the quote prints them (also each event of „Oferta łączona”,
 * `offer-print.ts`). `hideZeroLines`: „Ukryj zera” (`zero`, else a quantity of 0).
 */
export function printSections(sections: QuotePrint['sections'], hideZeroLines: boolean): PrintSection[] {
  let grey = 0;
  let first = true;
  const out: PrintSection[] = [];
  for (const s of sections) {
    const lines = printedLines(s.lines, hideZeroLines);
    if (lines.length === 0) continue;
    // the quote's own classification: the fixed „Obsługa” and sections of services only (versions before P8)
    const people = sectionGroup(s.lines.map((l) => l.type), (s.kind ?? 'equipment') as SectionKind) === 'services' && s.kind !== 'transport';
    const fill = people ? ACCENT : first ? INK : GREYS[grey++ % GREYS.length]!;
    if (!people) first = false;
    const sumGrosze = lines.reduce((a, l) => a + fullOf(l), 0);
    out.push({ title: s.name, short: firstWord(s.name), fill, sum: formatPln(sumGrosze), sumGrosze, people, rows: lines.map(rowOf) });
  }
  return out;
}

/** The place's name and address (each when there); an address that names the place already stands alone. */
export function placeLines(event: Pick<PrintEvent, 'location' | 'locationAddress'>): string[] {
  const place = [event.location?.trim(), event.locationAddress?.trim()].filter((p): p is string => !!p);
  // an address found on the map often starts with the place's name already
  if (place.length === 2 && place[1]!.toLocaleLowerCase('pl-PL').includes(place[0]!.toLocaleLowerCase('pl-PL'))) place.shift();
  return place;
}

/** The cells under the title: the place (owner 2026-10-31: no hours — no „Montaż · Próba”, no „Start · Demontaż”). */
function info(event: PrintEvent): InfoCell[] {
  const place = placeLines(event);
  return place.length > 0 ? [{ label: 'Miejsce', lines: place }] : [];
}

/** The big date and its note of an event (start and end as the user may see them). */
export function printDates(event: Pick<PrintEvent, 'startsAt' | 'endsAt'>): { bigDate: string | null; dateNote: string | null } {
  if (!event.startsAt) return { bigDate: null, dateNote: null };
  const start = new Date(event.startsAt);
  // an event ending in the night (before 06:00) belongs to its evening
  const endRaw = event.endsAt ? new Date(event.endsAt) : start;
  const end = Number(time.format(endRaw).slice(0, 2)) < 6 ? new Date(endRaw.getTime() - 6 * 3_600_000) : endRaw;
  const sameDay = dayKey.format(start) === dayKey.format(end) || end < start;
  if (sameDay) return { bigDate: dayMonth.format(start), dateNote: `${weekday.format(start)}, ${year.format(start)}` };
  const [sd, sm] = dayMonth.format(start).split('.');
  const [ed, em] = dayMonth.format(end).split('.');
  return { bigDate: sm === em ? `${sd}–${ed}.${em}` : `${sd}.${sm}–${ed}.${em}`, dateNote: `${weekday.format(start)} – ${weekday.format(end)}, ${year.format(end)}` };
}

/** The footer's lines: name, address, NIP · phone, e-mail, www (blank parts left out). */
export function companyLines(c: PrintCompany): string[] {
  const address = [c.addressL1, c.addressL2].filter(Boolean).join(', ');
  const first = [c.name, address, c.nip ? `NIP ${c.nip}` : ''].filter(Boolean).join(' · ');
  const second = [c.phone, c.email, c.www].filter(Boolean).join(' · ');
  return [first, second].filter(Boolean);
}

/** „30 października 2026” and „ważny do” (null when the validity is not set). */
export function issueDates(printedAt: string | Date, validDays: number): { date: string; validUntil: string | null } {
  const issued = new Date(printedAt);
  return { date: longDate.format(issued), validUntil: validDays > 0 ? fullDay.format(new Date(issued.getTime() + validDays * 86_400_000)) : null };
}

/** The terms with `{vat}` and `{waznosc}` filled in. */
export const termsText = (terms: string, vatBp: number, validDays: number): string => terms.replaceAll('{vat}', pctOf(vatBp)).replaceAll('{waznosc}', String(validDays)).trim();

/** The structure bar: a part per equipment section and one „Usługi”, from the full amounts. */
export function structureOf(parts: ReadonlyArray<{ short: string; fill: string; grosze: number }>, discounted: boolean): PrintModel['structure'] {
  const shown = parts.filter((p) => p.grosze > 0);
  const total = shown.reduce((a, p) => a + p.grosze, 0);
  if (total <= 0) return null;
  const pcts = percents(shown.map((p) => p.grosze));
  return { total: `${formatPln(total)} ${discounted ? 'przed rabatem' : 'netto'}`, parts: shown.map((p, i) => ({ short: p.short, pct: `${pcts[i]}%`, share: pcts[i]!, fill: p.fill })) };
}

export function printModel(data: QuotePrint, event: PrintEvent): PrintModel {
  const hide = data.version.hideZeroLines;
  const isGear = (s: QuotePrint['sections'][number]) => sectionGroup(s.lines.map((l) => l.type), (s.kind ?? 'equipment') as SectionKind) === 'equipment';
  // review: transport is a service wherever it stands (an equipment section of a version before P8 too)
  const gearSections = data.sections.filter(isGear).map((s) => ({ ...s, lines: s.lines.filter((l) => !l.transportLine) }));
  const sections = printSections(gearSections, hide);
  const gearLines = gearSections.flatMap((s) => printedLines(s.lines, hide));
  const movedTransport = data.sections.filter(isGear).flatMap((s) => s.lines.filter((l) => l.transportLine));
  const serviceLines = printedLines([...data.sections.filter((s) => !isGear(s)).flatMap((s) => s.lines), ...movedTransport], hide);
  const gear = groupMoney(gearLines);
  const people = groupMoney(serviceLines);
  const groups: PrintGroup[] = [];
  if (sections.length > 0) groups.push({ key: 'equipment', title: 'SPRZĘT', list: false, sections, summary: summaryRows(SPRZET, gear.fullGrosze, [{ label: discountLabel(gear.commonBp), grosze: gear.discountGrosze }], gear.netGrosze) });
  if (serviceLines.length > 0) groups.push({ key: 'services', title: 'USŁUGI', list: true, sections: [serviceList(serviceLines)], summary: summaryRows(USLUGI, people.fullGrosze, [{ label: discountLabel(people.commonBp), grosze: people.discountGrosze }], people.netGrosze) });
  const t = data.totals;
  const scopes = (event.scopes ?? []).filter((s) => s.trim() !== '');
  return {
    number: `wersja ${data.version.number}`,
    draft: data.version.state === 'robocza',
    ...issueDates(data.printedAt, data.validDays),
    eyebrow: scopes.length > 0 ? scopes.join(' · ') : data.subtitle.trim() || null,
    title: event.name ?? '',
    client: event.client?.trim() || null,
    ...printDates(event),
    info: info(event),
    structure: structureOf([...sections.map((s) => ({ short: s.short, fill: s.fill, grosze: s.sumGrosze })), { short: 'Usługi', fill: ACCENT, grosze: people.fullGrosze }], gear.discountGrosze !== 0 || people.discountGrosze !== 0),
    groups,
    totals: t
      ? {
          rows: [
            { label: gear.discountGrosze !== 0 ? SPRZET.after : 'Sprzęt', amount: formatPln(gear.netGrosze) },
            { label: people.discountGrosze !== 0 ? USLUGI.after : 'Usługi', amount: formatPln(people.netGrosze) },
            { label: 'Razem netto', amount: formatPln(t.netGrosze), big: true },
            { label: `VAT ${pctOf(data.version.vatBp)}`, amount: formatPln(t.vatGrosze) },
          ],
          gross: formatPln(t.grossGrosze),
        }
      : null,
    terms: termsText(data.terms, data.version.vatBp, data.validDays),
    company: companyLines(data.company),
  };
}
